// ── TimelineEditor — 24시간 타임라인 드래그앤드롭 ────────────
// 빈 영역 드래그 → 새 블록 생성 (근무/휴식 모드)
// 휴식 모드: work 블록 안에서 드래그 → 새 break 블록
// 블록 양 끝 핸들 드래그 → 리사이즈
// 블록 가운데 드래그 → 이동
// × 버튼 클릭 → 블록 삭제
// 30분 스냅. 분할 근무 + 다중 휴식 지원.

import React, { useState, useRef } from 'react';

const VIEW_W = 720;
const VIEW_H = 110;
const HOUR_W = VIEW_W / 24;
const TRACK_Y = 28;
const TRACK_H = 60;
const SNAP_MIN = 30;
const EDGE_PX = 12;        // 리사이즈 영역 (가장자리)

function timeToMin(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}
function minToTime(m) {
  m = Math.max(0, Math.min(24 * 60, m));
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}
const snap = m => Math.round(m / SNAP_MIN) * SNAP_MIN;
const minToX = m => (m / 60) * HOUR_W;

function mergeBlocks(blocks) {
  if (!blocks?.length) return [];
  const sorted = [...blocks].sort((a, b) => timeToMin(a.start) - timeToMin(b.start));
  const out = [];
  for (const b of sorted) {
    const last = out[out.length - 1];
    if (last && last.type === b.type && timeToMin(b.start) <= timeToMin(last.end)) {
      last.end = minToTime(Math.max(timeToMin(last.end), timeToMin(b.end)));
    } else {
      out.push({ ...b });
    }
  }
  return out;
}

function sumMinutes(blocks, type) {
  return (blocks || []).filter(b => b.type === type)
    .reduce((s, b) => s + (timeToMin(b.end) - timeToMin(b.start)), 0);
}

function shiftToBlocks(shift) {
  if (!shift) return [];
  if (Array.isArray(shift.blocks)) return shift.blocks;
  if (shift.isOff) return [];
  if (!shift.startTime || !shift.endTime) return [];
  const sM = timeToMin(shift.startTime);
  let eM = timeToMin(shift.endTime);
  if (eM <= sM) eM = 24 * 60;
  const blocks = [{ type: 'work', start: shift.startTime, end: minToTime(eM) }];
  const breakM = Number(shift.breakMinutes) || 0;
  if (breakM > 0 && eM - sM > breakM) {
    const mid = (sM + eM) / 2;
    blocks.push({
      type: 'break',
      start: minToTime(snap(mid - breakM / 2)),
      end:   minToTime(snap(mid + breakM / 2)),
    });
  }
  return blocks;
}

export default function TimelineEditor({ shift = {}, onSave, onClear, onCancel, date }) {
  const [blocks, setBlocks] = useState(() => shiftToBlocks(shift));
  const [mode, setMode] = useState('work');
  const [isOff, setIsOff] = useState(!!shift.isOff);
  const [isHoliday, setIsHoliday] = useState(!!shift.isHoliday);
  const [drag, setDrag] = useState(null);
  const svgRef = useRef(null);

  const workMin = sumMinutes(blocks, 'work');
  const breakMin = sumMinutes(blocks, 'break');
  const netMin = Math.max(0, workMin - breakMin);

  function clientToMin(clientX) {
    const rect = svgRef.current.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(24 * 60, snap(ratio * 24 * 60)));
  }
  function clientToPx(clientX) {
    const rect = svgRef.current.getBoundingClientRect();
    return clientX - rect.left;
  }

  // 블록 위 클릭 위치 분석: 'edge-start' | 'edge-end' | 'middle' | null
  function detectHit(blockIdx, clientX) {
    const b = blocks[blockIdx];
    const rect = svgRef.current.getBoundingClientRect();
    const px = clientToPx(clientX);
    const startPx = (timeToMin(b.start) / (24 * 60)) * rect.width;
    const endPx = (timeToMin(b.end) / (24 * 60)) * rect.width;
    if (px - startPx < EDGE_PX) return 'edge-start';
    if (endPx - px < EDGE_PX) return 'edge-end';
    return 'middle';
  }

  function findBlockAt(m) {
    return blocks.findIndex(b => timeToMin(b.start) <= m && m < timeToMin(b.end));
  }

  function onPointerDown(e) {
    if (e.button === 2) return;
    e.preventDefault();
    e.target.setPointerCapture?.(e.pointerId);
    const m = clientToMin(e.clientX);
    const workIdx = blocks.findIndex(b => b.type === 'work' && timeToMin(b.start) <= m && m < timeToMin(b.end));
    const breakIdx = blocks.findIndex(b => b.type === 'break' && timeToMin(b.start) <= m && m < timeToMin(b.end));

    // 1) break 블록 위 클릭 — 항상 break 편집 (모드 무관)
    if (breakIdx >= 0) {
      const hit = detectHit(breakIdx, e.clientX);
      const bb = blocks[breakIdx];
      const startM = timeToMin(bb.start), endM = timeToMin(bb.end);
      if (hit === 'edge-start') setDrag({ kind: 'resize-start', idx: breakIdx, originalStart: startM, originalEnd: endM });
      else if (hit === 'edge-end') setDrag({ kind: 'resize-end', idx: breakIdx, originalStart: startM, originalEnd: endM });
      else setDrag({ kind: 'move', idx: breakIdx, anchorM: m, originalStart: startM, originalEnd: endM });
      return;
    }

    // 2) work 블록 위 클릭
    if (workIdx >= 0) {
      const wb = blocks[workIdx];
      const wsM = timeToMin(wb.start), weM = timeToMin(wb.end);

      // 휴식 모드 → work 안에서 새 break 블록 생성
      if (mode === 'break') {
        const newBlock = {
          type: 'break',
          start: minToTime(m),
          end: minToTime(Math.min(weM, m + SNAP_MIN)),
        };
        const newBlocks = [...blocks, newBlock];
        setBlocks(newBlocks);
        setDrag({ kind: 'create-break', idx: newBlocks.length - 1, anchorM: m, parentEnd: weM });
        return;
      }

      // 근무 모드 → work 블록 편집
      const hit = detectHit(workIdx, e.clientX);
      if (hit === 'edge-start') setDrag({ kind: 'resize-start', idx: workIdx, originalStart: wsM, originalEnd: weM });
      else if (hit === 'edge-end') setDrag({ kind: 'resize-end', idx: workIdx, originalStart: wsM, originalEnd: weM });
      else setDrag({ kind: 'move', idx: workIdx, anchorM: m, originalStart: wsM, originalEnd: weM });
      return;
    }

    // 3) 빈 영역 — 근무 모드만 새 work 블록 생성
    if (mode === 'work') {
      const newBlock = { type: 'work', start: minToTime(m), end: minToTime(m + SNAP_MIN) };
      const newBlocks = [...blocks, newBlock];
      setBlocks(newBlocks);
      setDrag({ kind: 'create', idx: newBlocks.length - 1, anchorM: m });
    }
  }

  function onPointerMove(e) {
    if (!drag) return;
    e.preventDefault();
    const m = clientToMin(e.clientX);
    const next = blocks.map((b, i) => {
      if (i !== drag.idx) return b;
      let s = timeToMin(b.start), eM = timeToMin(b.end);
      if (drag.kind === 'create' || drag.kind === 'create-break') {
        const lo = Math.min(drag.anchorM, m);
        const hi = Math.max(drag.anchorM, m);
        s = lo;
        eM = Math.max(lo + SNAP_MIN, hi);
        if (drag.kind === 'create-break' && drag.parentEnd) {
          eM = Math.min(eM, drag.parentEnd);
        }
      } else if (drag.kind === 'resize-start') {
        s = Math.min(m, drag.originalEnd - SNAP_MIN);
      } else if (drag.kind === 'resize-end') {
        eM = Math.max(m, drag.originalStart + SNAP_MIN);
      } else if (drag.kind === 'move') {
        const delta = m - drag.anchorM;
        const len = drag.originalEnd - drag.originalStart;
        s = Math.max(0, Math.min(24 * 60 - len, drag.originalStart + delta));
        eM = s + len;
      }
      return { ...b, start: minToTime(s), end: minToTime(eM) };
    });
    setBlocks(next);
  }

  function onPointerUp() {
    if (!drag) return;
    const cleaned = mergeBlocks(blocks).filter(b => timeToMin(b.end) - timeToMin(b.start) > 0);
    // break은 work 안에 있어야
    const works = cleaned.filter(b => b.type === 'work');
    const breaks = cleaned.filter(b => b.type === 'break').filter(br => {
      const bs = timeToMin(br.start), be = timeToMin(br.end);
      return works.some(w => timeToMin(w.start) <= bs && be <= timeToMin(w.end));
    });
    setBlocks([...works, ...breaks]);
    setDrag(null);
  }

  function deleteBlock(idx) {
    setBlocks(blocks.filter((_, i) => i !== idx));
  }
  function clearAll() { setBlocks([]); }

  function handleSave() {
    const merged = mergeBlocks(blocks);
    const works = merged.filter(b => b.type === 'work').sort((a, b) => timeToMin(a.start) - timeToMin(b.start));
    const totalBreak = sumMinutes(merged, 'break');
    onSave({
      blocks: merged,
      isOff,
      isHoliday,
      ...(works.length ? {
        startTime: works[0].start,
        endTime: works[works.length - 1].end,
        breakMinutes: totalBreak,
      } : {}),
    });
  }

  const hourLabels = [0, 3, 6, 9, 12, 15, 18, 21, 24];

  return (
    <div onClick={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: '12px', padding: '20px',
                 maxWidth: '780px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#18181B', margin: '0 0 4px' }}>
          {date} 근무 입력
        </h3>
        <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 14px' }}>
          드래그로 새 블록 · 양 끝(║) 드래그 → 리사이즈 · 가운데 드래그 → 이동 · ×로 삭제
        </p>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                        marginBottom: '12px', cursor: 'pointer', padding: '8px',
                        background: isOff ? '#f3f4f6' : 'transparent', borderRadius: '6px' }}>
          <input type="checkbox" checked={isOff} onChange={e => setIsOff(e.target.checked)} />
          <span><strong>OFF</strong> (휴무일)</span>
        </label>

        {!isOff && (
          <>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
              <button onClick={() => setMode('work')}
                style={{ flex: 1, padding: '8px', borderRadius: '6px',
                         border: mode === 'work' ? 'none' : '1px solid #e5e7eb',
                         background: mode === 'work' ? '#FF6F0F' : '#fff',
                         color: mode === 'work' ? '#fff' : '#374151',
                         fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                💼 근무 (빈 영역 드래그)
              </button>
              <button onClick={() => setMode('break')}
                style={{ flex: 1, padding: '8px', borderRadius: '6px',
                         border: mode === 'break' ? 'none' : '1px solid #e5e7eb',
                         background: mode === 'break' ? '#A1A1AA' : '#fff',
                         color: mode === 'break' ? '#fff' : '#374151',
                         fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                ☕ 휴식 (근무 안 드래그)
              </button>
            </div>

            <div style={{ overflowX: 'auto', marginBottom: '12px',
                          border: '1px solid #e5e7eb', borderRadius: '8px',
                          background: '#fafaf9' }}>
              <svg ref={svgRef} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                style={{ width: '100%', minWidth: '500px', height: VIEW_H, display: 'block', touchAction: 'none' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onContextMenu={e => e.preventDefault()}>
                {Array.from({ length: 25 }).map((_, h) => (
                  <line key={h} x1={h * HOUR_W} y1={TRACK_Y} x2={h * HOUR_W} y2={TRACK_Y + TRACK_H}
                    stroke="#e5e7eb" strokeWidth="1" />
                ))}
                {hourLabels.map(h => (
                  <g key={h}>
                    <line x1={h * HOUR_W} y1={20} x2={h * HOUR_W} y2={26}
                      stroke="#9ca3af" strokeWidth="1" />
                    <text x={h * HOUR_W} y={16} fontSize="10" fill="#6b7280" textAnchor="middle">{h}</text>
                  </g>
                ))}
                <rect x="0" y={TRACK_Y} width={VIEW_W} height={TRACK_H} fill="#fff" stroke="#e5e7eb" />
                {/* 야간 음영 (22~06) */}
                <rect x={22 * HOUR_W} y={TRACK_Y} width={2 * HOUR_W} height={TRACK_H}
                  fill="rgba(99, 102, 241, 0.08)" pointerEvents="none" />
                <rect x="0" y={TRACK_Y} width={6 * HOUR_W} height={TRACK_H}
                  fill="rgba(99, 102, 241, 0.08)" pointerEvents="none" />

                {/* work 블록 */}
                {blocks.map((b, idx) => {
                  if (b.type !== 'work') return null;
                  const x = minToX(timeToMin(b.start));
                  const w = minToX(timeToMin(b.end)) - x;
                  return (
                    <g key={`w-${idx}`}>
                      <rect x={x} y={TRACK_Y + 4} width={w} height={TRACK_H - 8}
                        fill="#FF6F0F" rx="3" />
                      {/* 좌 핸들 */}
                      <rect x={x} y={TRACK_Y + 4} width={3} height={TRACK_H - 8}
                        fill="#fff" opacity="0.7" pointerEvents="none" />
                      {/* 우 핸들 */}
                      <rect x={x + w - 3} y={TRACK_Y + 4} width={3} height={TRACK_H - 8}
                        fill="#fff" opacity="0.7" pointerEvents="none" />
                      <text x={x + w / 2} y={TRACK_Y + TRACK_H / 2 + 4} fontSize="10"
                        fill="#fff" textAnchor="middle" pointerEvents="none">
                        {b.start}~{b.end}
                      </text>
                      <circle cx={x + w - 10} cy={TRACK_Y + 14} r="7" fill="#fff" stroke="#FF6F0F"
                        onPointerDown={ev => { ev.stopPropagation(); }}
                        onClick={ev => { ev.stopPropagation(); deleteBlock(idx); }}
                        style={{ cursor: 'pointer' }} />
                      <text x={x + w - 10} y={TRACK_Y + 18} fontSize="11" fill="#52525B"
                        textAnchor="middle" pointerEvents="none" fontWeight="700">×</text>
                    </g>
                  );
                })}

                {/* break 블록 (work 위에 겹침) */}
                {blocks.map((b, idx) => {
                  if (b.type !== 'break') return null;
                  const x = minToX(timeToMin(b.start));
                  const w = minToX(timeToMin(b.end)) - x;
                  return (
                    <g key={`b-${idx}`}>
                      <rect x={x} y={TRACK_Y + 16} width={w} height={TRACK_H - 32}
                        fill="#A1A1AA" rx="2" />
                      <rect x={x} y={TRACK_Y + 16} width={2} height={TRACK_H - 32}
                        fill="#fff" opacity="0.7" pointerEvents="none" />
                      <rect x={x + w - 2} y={TRACK_Y + 16} width={2} height={TRACK_H - 32}
                        fill="#fff" opacity="0.7" pointerEvents="none" />
                      <text x={x + w / 2} y={TRACK_Y + TRACK_H / 2 + 4} fontSize="9"
                        fill="#fff" textAnchor="middle" pointerEvents="none">
                        ☕ {b.start}~{b.end}
                      </text>
                      <circle cx={x + w - 8} cy={TRACK_Y + 22} r="6" fill="#fff" stroke="#A1A1AA"
                        onPointerDown={ev => { ev.stopPropagation(); }}
                        onClick={ev => { ev.stopPropagation(); deleteBlock(idx); }}
                        style={{ cursor: 'pointer' }} />
                      <text x={x + w - 8} y={TRACK_Y + 25} fontSize="10" fill="#52525B"
                        textAnchor="middle" pointerEvents="none" fontWeight="700">×</text>
                    </g>
                  );
                })}
              </svg>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
              <Stat label="근무" value={`${(workMin / 60).toFixed(2)}h`} color="#FF6F0F" />
              <Stat label="휴식" value={`${(breakMin / 60).toFixed(2)}h`} color="#A1A1AA" />
              <Stat label="실근무" value={`${(netMin / 60).toFixed(2)}h`} color="#0f766e" />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                            cursor: 'pointer', padding: '8px', background: '#fef2f2',
                            borderRadius: '6px', marginBottom: '12px' }}>
              <input type="checkbox" checked={isHoliday} onChange={e => setIsHoliday(e.target.checked)} />
              <span>🏖️ 휴일근무 (공휴일·약정 휴일)</span>
            </label>

            <button onClick={clearAll}
              style={{ width: '100%', padding: '6px', marginBottom: '12px', borderRadius: '6px',
                       border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280',
                       fontSize: '12px', cursor: 'pointer' }}>
              모든 블록 비우기
            </button>
          </>
        )}

        <div style={{ display: 'flex', gap: '6px', justifyContent: 'space-between' }}>
          <button onClick={onClear}
            style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #E4E4E7',
                     background: '#fff5f5', color: '#52525B', fontSize: '13px', cursor: 'pointer' }}>
            🗑️ 이 날짜 비우기
          </button>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={onCancel}
              style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #e5e7eb',
                       background: '#fff', cursor: 'pointer', fontSize: '13px' }}>취소</button>
            <button onClick={handleSave}
              style={{ padding: '8px 14px', borderRadius: '6px', border: 'none',
                       background: '#FF6F0F', color: '#fff', fontSize: '13px',
                       fontWeight: '700', cursor: 'pointer' }}>저장</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#fafaf9', borderRadius: '6px', padding: '8px 10px', textAlign: 'center' }}>
      <div style={{ fontSize: '10px', color: '#9ca3af' }}>{label}</div>
      <div style={{ fontSize: '14px', fontWeight: '700', color, marginTop: '2px' }}>{value}</div>
    </div>
  );
}
